import React, { useRef, useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { FilterMatchMode } from "primereact/api";
import { Menu } from "primereact/menu";
import {
  deleteCategory,
  getCategory,
} from "../../store/AsyncMethods/CategoryMethod";
import { ConfirmDialog } from "primereact/confirmdialog";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import OrderStatusChangeDialog from "./OrderStatusChangeDialog";
import { paginatorTemplate } from "../../components/OtherComponents/PaginatorTemplate";
import {
  changeOrderPaymentStatus,
  changeOrderStatus,
  deleteMultipleOrders,
  getOrders,
  getspecificOrder,
} from "../../store/AsyncMethods/OrdersMethod";
import { getSetupDetails } from "../../store/AsyncMethods/SetupMethod";
import InputFocus from "../../hooks/InputFocus";
import { Dropdown } from "primereact/dropdown";
import { Tooltip } from "primereact/tooltip";
import clsx from "clsx";
import ShippingLabel from "../../components/OtherComponents/ShippingLabel";
import Invoice from "../../components/OtherComponents/Invoice"; // 1. IMPORT INVOICE COMPONENT
import store from "../../store";

export default function OrdersTable() {
  const { allOrders, specificOrder } = useSelector(
    (state) => state.OrderReducer
  );
  const { setupDetails } = useSelector((state) => state.SetupReducer);

  const { enableCurrency } = useSelector((state) => state.CurrencyReducer);

  const menu = useRef(null);
  const inputRef = InputFocus();
  const [selectedItem, setSelectedItem] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showDelDialog, setShowDelDialog] = useState(false);
  const [showMultipleDelDialog, setShowMultipleDelDialog] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [paymentDialogVisible, setPaymentDialogVisible] = useState(false);
  const [showShippingLabel, setShowShippingLabel] = useState(false);
  const [showBulkShippingLabel, setShowBulkShippingLabel] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false); // 2. ADD INVOICE STATE
  const [showBulkInvoices, setShowBulkInvoices] = useState(false); // 2. ADD BULK INVOICE STATE
  const [bulkOrders, setBulkOrders] = useState([]);
  const [showPrintAll, setShowPrintAll] = useState(false); // ADD PRINT ALL STATE

  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [trackingId, setTrackingId] = useState("");
  const [courierCompany, setCourierCompany] = useState("");

  // Courier company options
  const courierOptions = [
    { label: "Select Courier Company", value: "" },
    { label: "Leopard Courier", value: "Leopard Courier" },
    { label: "TCS", value: "TCS" },
    { label: "DHL", value: "DHL" },
    { label: "FedEx", value: "FedEx" },
    { label: "BlueEx", value: "BlueEx" },
    { label: "Call Courier", value: "Call Courier" },
    { label: "Trax", value: "Trax" },
    { label: "Other", value: "Other" }
  ];

  // Tab configuration
  const tabHeaders = [
    {
      label: "Order Processing",
      icon: "pi pi-box",
      description: "Prepare and package items for shipping",
      color: "blue",
      status: "to_pack",
    },
    {
      label: "Shipment Planning",
      icon: "pi pi-truck",
      description: "Schedule pickup with logistics partner",
      color: "purple",
      status: "arrange_shipment",
    },
    {
      label: "Handover",
      icon: "pi pi-send",
      description: "Transfer package to courier service",
      color: "orange",
      status: "hand_over",
    },
    {
      label: "In Transit",
      icon: "pi pi-map-marker",
      description: "Track package delivery status",
      color: "green",
      status: "shipping",
    },
    {
      label: "Delivered",
      icon: "pi pi-check-circle",
      description: "Successfully delivered orders",
      color: "emerald",
      status: "delivered",
    },
    {
      label: "Cancelled",
      icon: "pi pi-times-circle",
      description: "Cancelled orders",
      color: "red",
      status: "cancelled",
    },
  ];

  // Map tab index to status for filtering
  const tabStatusMap = tabHeaders.map(tab => tab.status);

  // For dialog and status change
  const allStatusOptions = [
    { name: "To Pack", value: "to_pack" },
    { name: "Arrange Shipment", value: "arrange_shipment" },
    { name: "Hand Over", value: "hand_over" },
    { name: "Shipping", value: "shipping" },
    { name: "Delivered", value: "delivered" },
    { name: "Cancelled", value: "cancelled" },
  ];

  // Function to get filtered status options based on selected orders
  const getFilteredStatusOptions = (selectedOrders) => {
    if (!selectedOrders || selectedOrders.length === 0) {
      return allStatusOptions;
    }

    // Check if any selected order is delivered
    const hasDeliveredOrders = selectedOrders.some(order => order.status === 'delivered');
    
    if (hasDeliveredOrders) {
      // If any order is delivered, only allow delivered or cancelled status
      return allStatusOptions.filter(option => 
        option.value === 'delivered' || option.value === 'cancelled'
      );
    }

    // For non-delivered orders, allow all status options
    return allStatusOptions;
  };

  // Get current filtered status options
  const statusOptions = getFilteredStatusOptions(selectedProducts);

  const paymentStatusOptions = [
    { name: "Paid", value: 1 },
    { name: "Not Paid", value: 0 },
  ];
  useEffect(() => {
    dispatch(getSetupDetails());
  }, []);

  // Get orders for current tab
  const getOrdersForTab = (tabIndex) => {
    const tabStatus = tabHeaders[tabIndex]?.status;
    if (!tabStatus) return [];

    return allOrders.filter((order) => {
      // Check if order status matches tab status
      let statusMatches = order.status === tabStatus;

      // Special case: 'pending' orders should also appear in 'to_pack' tab
      if (tabStatus === "to_pack" && order.status === "pending") {
        statusMatches = true;
      }

      // If status doesn't match, filter out the order
      if (!statusMatches) return false;

      // Apply courier company filter
      if (courierCompany && courierCompany.trim() !== "") {
        if (!order.courier_company || order.courier_company !== courierCompany) {
          return false;
        }
      }

      // Apply search filter
      return (
        searchTerm.trim() === "" ||
        (order.name &&
          order.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (order.order_number &&
          order.order_number.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    });
  };

  const currentTabOrders = getOrdersForTab(activeTab);

  // Status change handlers for bulk operations
  const handleMoveToNextTab = async () => {
    if (!selectedProducts || selectedProducts.length === 0) {
      toast.info("No orders selected.");
      return;
    }

    const currentStatus = tabHeaders[activeTab]?.status;
    let nextStatus = "";
    let trackingRequired = false;

    switch (currentStatus) {
      case "to_pack":
        nextStatus = "arrange_shipment";
        break;
      case "arrange_shipment":
        nextStatus = "hand_over";
        break;
      case "hand_over":
        nextStatus = "shipping";
        trackingRequired = true;
        break;
      case "shipping":
        nextStatus = "delivered";
        break;
      default:
        toast.error("Cannot move orders from this status.");
        return;
    }

    if (trackingRequired && !trackingId.trim()) {
      toast.error("Tracking ID is required for handover.");
      return;
    }

    if (trackingRequired && !courierCompany.trim()) {
      toast.error("Courier company is required for handover.");
      return;
    }

    const idsArray = selectedProducts.map((obj) => obj.id);
    const data = {
      orders: idsArray,
      status: nextStatus,
      ...(trackingRequired && { tracking_id: trackingId, courier_company: courierCompany }),
    };

    dispatch(changeOrderStatus(data)).then((success) => {
      if (success) {
        setSelectedProducts(null);
        setTrackingId("");
        setCourierCompany("");
        dispatch(getOrders());
        toast.success(
          `Orders moved to ${
            tabHeaders.find((tab) => tab.status === nextStatus)?.label
          }!`
        );
      }
    });
  };

  const handleMoveToPreviousTab = async () => {
    if (!selectedProducts || selectedProducts.length === 0) {
      toast.info("No orders selected.");
      return;
    }

    const currentStatus = tabHeaders[activeTab]?.status;
    let previousStatus = "";

    // Prevent moving delivered orders to previous status
    if (currentStatus === "delivered") {
      toast.error("Delivered orders cannot be reverted to earlier statuses.");
      return;
    }

    switch (currentStatus) {
      case "arrange_shipment":
        previousStatus = "to_pack";
        break;
      case "hand_over":
        previousStatus = "arrange_shipment";
        break;
      case "shipping":
        previousStatus = "hand_over";
        break;
      default:
        toast.error("Cannot move orders from this status.");
        return;
    }

    const idsArray = selectedProducts.map((obj) => obj.id);
    const data = {
      orders: idsArray,
      status: previousStatus,
    };

    dispatch(changeOrderStatus(data)).then((success) => {
      if (success) {
        setSelectedProducts(null);
        dispatch(getOrders());
        toast.success(
          `Orders moved to ${
            tabHeaders.find((tab) => tab.status === previousStatus)?.label
          }!`
        );
      }
    });
  };

  const closeStatusDialog = () => {
    setDialogVisible(false);
  };

  const closePaymentDialog = () => {
    setPaymentDialogVisible(false);
  };

  const accept = () => {
    dispatch(deleteCategory(selectedItem?.id)).then((success) => {
      if (success) {
        setSelectedItem(null);
        dispatch(getCategory());
      }
    });
  };

  const reject = () => {
    setShowDelDialog(false);
  };

  const acceptMultiple = () => {
    if (selectedProducts) {
      const idsArray = selectedProducts.map((obj) => obj.id);

      if (selectedProducts?.length !== 0) {
        dispatch(deleteMultipleOrders(idsArray)).then((success) => {
          if (success) {
            setSelectedProducts(null);
            dispatch(getOrders());
          }
        });
      }
    } else {
      toast.info("No any record selected.");
    }
  };

  const isChangeDisabled = () => {
    return selectedProducts?.some(
      (product) =>
        product.status === "delivered" && product.payment_status === 1
    );
  };

  const isHandoverOrBeyond = () => {
    return selectedProducts?.some(
      (product) =>
        product.status === "hand_over" ||
        product.status === "shipping" ||
        product.status === "delivered" ||
        product.status === "cancelled"
    );
  };

  const rejectMultiple = () => {
    setShowMultipleDelDialog(false);
  };

  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const onGlobalFilterChange = (event) => {
    const value = event.target.value;
    let _filters = { ...filters };

    _filters["global"].value = value;

    setFilters(_filters);
  };

  const handleStatusChange = () => {
    if (selectedProducts) {
      const idsArray = selectedProducts.map((obj) => obj.id);

      if (selectedProducts?.length !== 0) {
        setDialogVisible(true);
      } else {
        toast.info("No any record selected.");
      }
    } else {
      toast.info("No any record selected.");
    }
  };

  const handlePaymentStatusChange = () => {
    if (selectedProducts) {
      const idsArray = selectedProducts.map((obj) => obj.id);

      if (selectedProducts?.length !== 0) {
        setPaymentDialogVisible(true);
      } else {
        toast.info("No any record selected.");
      }
    } else {
      toast.info("No any record selected.");
    }
  };

  const handlePrintShippingLabel = () => {
    window.print();
    setShowShippingLabel(false);
  };

  const handlePrintBulkShippingLabels = () => {
    // The PDF download is now handled by the ShippingLabel component
    // Just close the modal after a short delay
    setTimeout(() => {
      setShowBulkShippingLabel(false);
    }, 1000);
  };

  const handleBulkShippingLabel = () => {
    if (selectedProducts && selectedProducts.length > 0) {
      // For bulk shipping labels, we'll show all labels in one modal
      toast.info(
        `Preparing ${selectedProducts.length} shipping labels for printing...`
      );

      // Get all selected orders data
      const fetchAllOrders = async () => {
        const ordersData = [];

        for (let i = 0; i < selectedProducts.length; i++) {
          const order = selectedProducts[i];
          try {
            // Fetch the specific order data
            await dispatch(getspecificOrder(order.id));

            // Wait for the state to update and get the specific order
            await new Promise((resolve) => setTimeout(resolve, 200));

            // Get the current specific order from Redux state
            const currentState = store.getState();
            const fetchedOrder = currentState.OrderReducer.specificOrder;

            // Create a complete order object with all necessary shipping information
            const completeOrder = {
              ...order,
              ...fetchedOrder,
              // Ensure all required fields for shipping label are present
              sales_order:
                fetchedOrder?.sales_order || order?.sales_order || "N/A",
              marketplace:
                fetchedOrder?.marketplace || order?.marketplace || "N/A",
              tracking_number:
                fetchedOrder?.tracking_id || order?.tracking_id || "N/A",
              routing_code:
                fetchedOrder?.routing_code || order?.routing_code || "N/A",
              service: fetchedOrder?.service || order?.service || "N/A",
              weight: fetchedOrder?.weight || order?.weight || "N/A",
              delivery_type:
                fetchedOrder?.delivery_type || order?.delivery_type || "N/A",
              payment_method:
                fetchedOrder?.payment_method || order?.payment_method || "N/A",
              currency: fetchedOrder?.currency || order?.currency || "",
              total_amount:
                fetchedOrder?.total_amount || order?.total_amount || "N/A",
              order_number:
                fetchedOrder?.order_number || order?.order_number || "N/A",
              created_at:
                fetchedOrder?.created_at || order?.created_at || "N/A",
              awb_print_date:
                fetchedOrder?.awb_print_date || order?.awb_print_date || "N/A",
              courier: fetchedOrder?.courier || order?.courier || "N/A",
              // Add recipient information
              recipient: {
                name: fetchedOrder?.name || order?.name || "N/A",
                address_line1:
                  fetchedOrder?.address_line1 || order?.address_line1 || "",
                address_line2:
                  fetchedOrder?.address_line2 || order?.address_line2 || "",
                city_line: fetchedOrder?.city_line || order?.city_line || "",
                area: fetchedOrder?.area || order?.area || "",
                phone: fetchedOrder?.phone || order?.phone || "",
              },
              // Add sender information
              sender: {
                name: fetchedOrder?.sender_name || order?.sender_name || "N/A",
                address_line1:
                  fetchedOrder?.sender_address_line1 ||
                  order?.sender_address_line1 ||
                  "",
                address_line2:
                  fetchedOrder?.sender_address_line2 ||
                  order?.sender_address_line2 ||
                  "",
                address_line3:
                  fetchedOrder?.sender_address_line3 ||
                  order?.sender_address_line3 ||
                  "",
                phone: fetchedOrder?.sender_phone || order?.sender_phone || "",
              },
            };

            ordersData.push(completeOrder);
          } catch (error) {
            console.error(`Error fetching order ${order.id}:`, error);
            // Add basic order data as fallback
            ordersData.push(order);
          }
        }

        setBulkOrders(ordersData);
        setShowBulkShippingLabel(true);
      };

      fetchAllOrders();
    } else {
      toast.info("No orders selected.");
    }
  };

  // 5. HANDLER FOR BULK INVOICES
  const handleBulkInvoices = () => {
    if (selectedProducts && selectedProducts.length > 0) {
      toast.info(`Preparing ${selectedProducts.length} invoices...`);

      const fetchAllOrdersForInvoice = async () => {
        const ordersData = [];
        for (const order of selectedProducts) {
          try {
            await dispatch(getspecificOrder(order.id));
            await new Promise((resolve) => setTimeout(resolve, 200));

            const currentState = store.getState();
            const fetchedOrder = currentState.OrderReducer.specificOrder;

            // Map products to the line_items structure required by the Invoice component
            const line_items = (fetchedOrder?.products || []).map(
              (product) => ({
                id: product.id,
                name: product.name,
                quantity: product.pivot?.quantity || 1,
                price:
                  parseFloat(product.pivot?.price) ||
                  parseFloat(product.price) ||
                  0,
                discount: parseFloat(product.pivot?.discount) || 0,
              })
            );

            const completeOrder = {
              ...order,
              ...fetchedOrder,
              line_items: line_items,
            };

            ordersData.push(completeOrder);
          } catch (error) {
            console.error(
              `Error fetching order ${order.id} for invoice:`,
              error
            );
            ordersData.push(order); // Fallback with basic data
          }
        }
        setBulkOrders(ordersData);
        setShowBulkInvoices(true);
      };

      fetchAllOrdersForInvoice();
    } else {
      toast.info("No orders selected.");
    }
  };

  const handlePrintAll = () => {
    if (allOrders && allOrders.length > 0) {
      // Use the same filtering logic as the table display
      let filteredOrders = getOrdersForTab(activeTab);
      
      if (filteredOrders.length > 0) {
        toast.info(`Preparing ${filteredOrders.length} orders for printing...`);
        setBulkOrders(filteredOrders);
        setShowPrintAll(true);
      } else {
        toast.info("No orders match the current filters.");
      }
    } else {
      toast.info("No orders available to print.");
    }
  };

  // Action bar for multi-select with tab-specific actions
  const actionBar = (
    <div className="flex flex-wrap gap-2 justify-between mt-6 mb-2">
      <div className="flex flex-wrap gap-2">
        {!isHandoverOrBeyond() && (
          <Button
            label="Delete"
            icon="pi pi-trash"
            className="p-button-sm p-red-btn text-white rounded-lg px-2 py-1 text-sm font-medium border-0 shadow-sm"
            onClick={() => setShowMultipleDelDialog(true)}
            disabled={!selectedProducts || selectedProducts.length === 0}
          />
        )}
        <Button
          label="Change Status"
          icon="pi pi-pencil"
          className="p-button-sm bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg px-2 py-1 text-sm font-medium border-0 shadow-sm"
          onClick={handleStatusChange}
          disabled={
            !selectedProducts ||
            selectedProducts.length === 0 ||
            isChangeDisabled()
          }
        />
        {!isHandoverOrBeyond() && (
          <Button
            label="Change Payment Status"
            icon="pi pi-pencil"
            className="p-button-sm bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg px-2 py-1 text-sm font-medium border-0 shadow-sm"
            onClick={handlePaymentStatusChange}
            disabled={
              !selectedProducts ||
              selectedProducts.length === 0 ||
              isChangeDisabled()
            }
          />
        )}
        <Button
          label="Print All"
          icon="pi pi-print"
          className="p-button-sm bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-2 py-1 text-sm font-medium border-0 shadow-sm"
          onClick={handlePrintAll}
          disabled={!allOrders || allOrders.length === 0}
        />
        <Button
          label="Generate Shipping Label"
          icon="pi pi-print"
          className="p-button-sm bg-purple-500 hover:bg-purple-600 text-white rounded-lg px-2 py-1 text-sm font-medium border-0 shadow-sm"
          onClick={handleBulkShippingLabel}
          disabled={!selectedProducts || selectedProducts.length === 0}
        />
        {/* 4. ADD GENERATE INVOICES BUTTON */}
        <Button
          label="Generate Invoices"
          icon="pi pi-file"
          className="p-button-sm bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg px-2 py-1 text-sm font-medium border-0 shadow-sm"
          onClick={handleBulkInvoices}
          disabled={!selectedProducts || selectedProducts.length === 0}
        />
      </div>

      {/* Tab-specific actions */}
      <div className="flex flex-wrap gap-2">
        {activeTab > 0 && tabHeaders[activeTab]?.status !== "delivered" && (
          <Button
            label="Move to Previous"
            icon="pi pi-arrow-left"
            className="p-button-sm bg-gray-500 hover:bg-gray-600 text-white rounded-lg px-2 py-1 text-sm font-medium border-0 shadow-sm"
            onClick={handleMoveToPreviousTab}
            disabled={!selectedProducts || selectedProducts.length === 0}
          />
        )}
        {activeTab < tabHeaders.length - 1 && (
          <Button
            label="Move to Next"
            icon="pi pi-arrow-right"
            className="p-button-sm bg-blue-500 hover:bg-blue-600 text-white rounded-lg px-2 py-1 text-sm font-medium border-0 shadow-sm"
            onClick={handleMoveToNextTab}
            disabled={!selectedProducts || selectedProducts.length === 0}
          />
        )}
      </div>
    </div>
  );

  const renderHeader = () => {
    const value = filters["global"] ? filters["global"].value : "";

    return (
      <div className="flex justify-between">
        <span className="p-input-icon-left flex w-10/12 sm:w-8/12 md:w-6/12 lg:w-4/12 xl:w-3/12 ">
          <InputText
            ref={inputRef}
            className="w-full p-primary-input"
            type="search"
            value={value || ""}
            onChange={(e) => onGlobalFilterChange(e)}
            placeholder="Search"
          />
        </span>
      </div>
    );
  };

  const header = renderHeader();

  let items = [
    {
      label: "Edit",
      icon: "pi pi-pencil",
      command: () => {
        navigate(`/edit-order/${selectedItem?.id}`);
      },
    },
    {
      label: "Shipping Label",
      icon: "pi pi-print",
      command: () => {
        dispatch(getspecificOrder(selectedItem?.id)).then(() => {
          setShowShippingLabel(true);
        });
      },
    },
    // 3. ADD INVOICE TO ACTION MENU
    {
      label: "Invoice",
      icon: "pi pi-file",
      command: () => {
        dispatch(getspecificOrder(selectedItem?.id)).then(() => {
          setShowInvoice(true);
        });
      },
    },
    {
      label: "Delete",
      icon: "pi pi-trash",
      command: () => {
        setShowDelDialog(true);
      },
    },
  ];

  const actionBtn = (rowData) => (
    <React.Fragment>
      <div className=" ">
        <i
          className="pi pi-cog text-secondary cursor-pointer"
          onClick={(e) => {
            menu.current.toggle(e);
            setSelectedItem(rowData);
          }}
        ></i>
        <Menu model={items} popup ref={menu} className="p-0 z-50" />
      </div>
    </React.Fragment>
  );

  const dateAndTimeBody = (rowData) => {
    const date = new Date(rowData.created_at);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear());
    let hour = date.getHours();
    const minute = String(date.getMinutes()).padStart(2, "0");
    let period = "AM";

    if (hour >= 12) {
      period = "PM";
      hour = hour === 12 ? hour : hour - 12;
    }

    hour = String(hour).padStart(2, "0");

    const formattedDate = `${day}-${month}-${year}, ${hour}:${minute} ${period}`;
    return formattedDate;
  };

  const formatStatus = (status) =>
    status
      ? status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
      : "";

  const statusBodyTemplate = (rowData) => {
    return (
      <Tag
        value={formatStatus(rowData.status)}
        className="capitalize"
        pt={{
          root: {
            className: getSeverity(rowData.status),
          },
        }}
      />
    );
  };

  const paymentMethodBodyTemplate = (rowData) => {
    return <Tag value={rowData.payment_method} className="capitalize" />;
  };

  const paymentStatusBodyTemplate = (rowData) => {
    return (
      <Tag
        value={rowData.payment_status === 1 ? "Paid" : "Not Paid"}
        className="capitalize"
        pt={{
          root: {
            className: getSeverity(rowData.payment_status),
          },
        }}
      />
    );
  };

  const getSeverity = (data) => {
    switch (data) {
      case "pending":
        return "bg-blue-500";
      case "proceed":
        return "bg-purple-500";
      case "delivered":
        return "bg-pink-500";
      case 1:
        return "bg-purple-500";
      case 0:
        return "bg-blue-500";
      default:
        return null;
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Order Management</h1>
        <Button
          label="Add Order"
          icon="pi pi-plus"
          className="p-primary-btn"
          onClick={() => navigate("/add-order")}
        />
      </div>

      {/* Custom Tab Header */}
      <div className="flex bg-white rounded-lg shadow mb-6 border border-gray-200 overflow-x-auto">
        {tabHeaders.map((tab, idx) => (
          <button
            key={tab.label}
            className={clsx(
              "flex flex-col items-center gap-2 px-6 py-4 text-base font-semibold transition-all duration-150 min-w-[200px]",
              "focus:outline-none",
              idx === activeTab
                ? `text-${tab.color}-600 border-b-4 border-${tab.color}-500 bg-${tab.color}-50 shadow-sm z-10`
                : `text-gray-600 hover:text-${tab.color}-500 border-b-4 border-transparent hover:bg-${tab.color}-50`,
              idx === 0 ? "rounded-l-lg" : "",
              idx === tabHeaders.length - 1 ? "rounded-r-lg" : ""
            )}
            onClick={() => setActiveTab(idx)}
            type="button"
          >
            <div className="flex items-center gap-2">
              <i className={clsx(tab.icon, "text-xl")}></i>
              <span>{tab.label}</span>
              <span className="bg-gray-200 text-gray-700 text-xs font-bold px-2 py-1 rounded-full">
                {getOrdersForTab(idx).length}
              </span>
            </div>
            <span className="text-sm font-normal text-gray-500">
              {tab.description}
            </span>
          </button>
        ))}
      </div>

      {/* Search Bar and Filters */}
      <div className="flex flex-col md:flex-row md:items-center gap-4 bg-white border border-gray-200 rounded-lg shadow-sm px-4 py-3 mb-6">
        <div className="flex items-center gap-2 flex-1">
          <span className="font-semibold text-gray-700">Search:</span>
          <InputText
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Order number or customer name"
            className="w-full md:w-72"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-700">Courier:</span>
          <Dropdown
            value={courierCompany}
            options={courierOptions}
            onChange={(e) => setCourierCompany(e.value)}
            placeholder="Filter by courier"
            className="w-full md:w-48"
            showClear
          />
        </div>
        {(searchTerm || courierCompany) && (
          <Button
            label="Clear Filters"
            icon="pi pi-times"
            className="p-button-outlined p-button-sm"
            onClick={() => {
              setSearchTerm("");
              setCourierCompany("");
            }}
          />
        )}
      </div>

      {/* Tracking ID and Courier Company Input for Handover Tab */}
      {activeTab === 3 && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm px-4 py-3 mb-6">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <span className="font-semibold text-gray-700">Courier Company:</span>
              <Dropdown
                value={courierCompany}
                options={courierOptions}
                onChange={(e) => setCourierCompany(e.value)}
                placeholder="Select courier company"
                className="w-full md:w-80"
              />
            </div>
            <div className="flex items-center gap-4">
              <span className="font-semibold text-gray-700">Tracking ID:</span>
              <InputText
                value={trackingId}
                onChange={(e) => setTrackingId(e.target.value)}
                placeholder="Enter tracking ID for selected orders"
                className="w-full md:w-80"
              />
            </div>
          </div>
        </div>
      )}

      {/* Action Bar */}
      {actionBar}

      {/* Table Card */}
      <div className="card bg-white rounded-xl shadow-lg border border-gray-200 overflow-x-auto">
        <DataTable
          value={currentTabOrders}
          tableStyle={{ minWidth: "50rem" }}
          stripedRows
          paginator
          rows={10}
          rowsPerPageOptions={[10, 25, 50]}
          className="rounded-xl"
          header={null}
          pt={{
            filterOperator: { className: "w-3" },
            footerRow: { className: "!bg-red-900" },
            row: {
              className:
                "hover:bg-orange-50 transition-all duration-150 cursor-pointer",
            },
          }}
          size="small"
          selection={selectedProducts}
          onSelectionChange={(e) => setSelectedProducts(e.value)}
          onRowClick={(e) => navigate(`/edit-order/${e.data.id}`)}
        >
          <Column
            selectionMode="multiple"
            headerStyle={{ width: "3rem" }}
            bodyClassName="!py-3"
          ></Column>
          <Column
            field="name"
            sortable
            header="Customer Name"
            bodyClassName="!py-3 font-semibold text-gray-800"
          />
          <Column
            field="order_number"
            header="Order Number"
            bodyClassName="!py-3 text-gray-700"
          />
          <Column
            field="total_amount"
            sortable
            header="Total Amount"
            body={(rowData) =>
              `${
                enableCurrency?.unit || ""
              } ${rowData?.total_amount?.toLocaleString("en-IN")}`
            }
            bodyClassName="!py-3 text-green-700 font-bold"
          />
          <Column
            header="Total Products"
            body={(rowData) => rowData?.products?.length}
            bodyClassName="!py-3 text-gray-700"
          />
          <Column
            field="status"
            sortable
            header="Status"
            body={(rowData) => (
              <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                {formatStatus(rowData.status)}
              </span>
            )}
            style={{ minWidth: "110px" }}
            className="capitalize"
            bodyClassName="!py-3"
          />
          <Column
            field="payment_status"
            sortable
            header="Payment Status"
            body={(rowData) => (
              <span
                className={
                  rowData.payment_status === 1
                    ? "inline-block px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700"
                    : "inline-block px-3 py-1 rounded-full text-xs font-semibold bg-gray-200 text-gray-700"
                }
              >
                {rowData.payment_status === 1 ? "Paid" : "Not Paid"}
              </span>
            )}
            style={{ minWidth: "110px" }}
            className="capitalize"
            bodyClassName="!py-3"
          />
          <Column
            field="payment_method"
            sortable
            header="Payment Method"
            bodyClassName="!py-3 text-gray-700"
          />
          <Column
            field="courier_company"
            sortable
            header="Courier Company"
            body={(rowData) => (
              <span className="text-sm text-blue-600 font-medium">
                {rowData.courier_company || 'N/A'}
              </span>
            )}
            bodyClassName="!py-3"
            style={{ minWidth: "120px" }}
          />
          <Column
            field="warehouse_name"
            sortable
            header="Fulfillment Warehouse"
            body={(rowData) => (
              <div className="flex flex-col">
                <span className="font-semibold text-gray-800">{rowData.warehouse_name || 'Default'}</span>
                <span className="text-xs text-gray-500">{rowData.warehouse_city || ''}</span>
              </div>
            )}
            bodyClassName="!py-3"
            style={{ minWidth: "150px" }}
          />
          <Column
            field="tracking_id"
            sortable
            header="Tracking ID"
            body={(rowData) => (
              <span className="text-sm text-orange-600 font-medium">
                {rowData.tracking_id || 'N/A'}
              </span>
            )}
            bodyClassName="!py-3"
            style={{ minWidth: "120px" }}
          />
          <Column
            field="created_at"
            sortable
            header="Date"
            body={dateAndTimeBody}
            bodyClassName="!py-3 text-gray-500"
          />
          <Column header="Action" body={actionBtn} bodyClassName="!py-3" />
        </DataTable>
      </div>
      <Tooltip target=".pi-cog" content="Actions" />

      <ConfirmDialog
        visible={showDelDialog}
        onHide={reject}
        message="Are you sure you want to delete this record?"
        header="Confirmation"
        icon="pi pi-exclamation-triangle"
        accept={accept}
        reject={reject}
      />

      <ConfirmDialog
        visible={showMultipleDelDialog}
        onHide={rejectMultiple}
        message="Are you sure you want to delete these records?"
        header="Confirmation"
        icon="pi pi-exclamation-triangle"
        accept={acceptMultiple}
        reject={rejectMultiple}
      />

      <Dialog
        visible={dialogVisible}
        header={
          <p className="text-2xl font-bold p-font-style text-color mr-6 text-black dark:text-white">
            Change Status
          </p>
        }
        onHide={closeStatusDialog}
        className="w-full sm:w-9/12 md:w-7/12 lg:w-5/12 xl:w-5/12 mx-2"
      >
        <OrderStatusChangeDialog
          selectedProducts={selectedProducts}
          setDialogVisible={setDialogVisible}
          setSelectedProducts={setSelectedProducts}
          changeOrderStatus={changeOrderStatus}
          statusOptions={statusOptions}
          closeDialog={closeStatusDialog}
        />
      </Dialog>
      <Dialog
        visible={paymentDialogVisible}
        header={
          <p className="text-2xl font-bold p-font-style text-color mr-6 text-black dark:text-white">
            Change Payment Status
          </p>
        }
        onHide={closePaymentDialog}
        className="w-full sm:w-9/12 md:w-7/12 lg:w-5/12 xl:w-5/12 mx-2"
      >
        <OrderStatusChangeDialog
          selectedProducts={selectedProducts}
          setDialogVisible={setPaymentDialogVisible}
          setSelectedProducts={setSelectedProducts}
          changeOrderStatus={changeOrderPaymentStatus}
          statusOptions={paymentStatusOptions}
          closeDialog={closePaymentDialog}
        />
      </Dialog>

      {/* 6. ADD INVOICE MODALS */}

      {/* Single Invoice Modal */}
      {showInvoice && specificOrder && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.4)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: 32,
              borderRadius: 8,
              boxShadow: "0 2px 16px rgba(0,0,0,0.2)",
              position: "relative",
              width: "90%",
              height: "90%",
              overflow: "auto",
            }}
          >
            <Button
              icon="pi pi-times"
              className="p-button-text no-print"
              style={{ position: "absolute", top: 8, right: 8, zIndex: 1001 }}
              onClick={() => setShowInvoice(false)}
            />
            <div className="w-full h-full">
              <Invoice
                orders={[
                  {
                    ...specificOrder,
                    // Map products to the line_items structure for the invoice
                    line_items: (specificOrder.products || []).map((p) => ({
                      id: p.id,
                      name: p.name,
                      quantity: p.pivot?.quantity || 1,
                      price:
                        parseFloat(p.pivot?.price) || parseFloat(p.price) || 0,
                      discount: parseFloat(p.pivot?.discount) || 0,
                    })),
                  },
                ]}
                companyDetails={setupDetails}
              />
            </div>
          </div>
        </div>
      )}

      {/* Bulk Invoices Modal */}
      {showBulkInvoices && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.4)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: 32,
              borderRadius: 8,
              boxShadow: "0 2px 16px rgba(0,0,0,0.2)",
              position: "relative",
              width: "90%",
              height: "90%",
              overflow: "auto",
            }}
          >
            <Button
              icon="pi pi-times"
              className="p-button-text no-print"
              style={{ position: "absolute", top: 8, right: 8, zIndex: 1001 }}
              onClick={() => setShowBulkInvoices(false)}
            />
            <div className="w-full h-full">
              <Invoice orders={bulkOrders} companyDetails={setupDetails} />
            </div>
          </div>
        </div>
      )}

      {/* Shipping Label Modal */}
      {showShippingLabel && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.4)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: 32,
              borderRadius: 8,
              boxShadow: "0 2px 16px rgba(0,0,0,0.2)",
              position: "relative",
            }}
          >
            <Button
              icon="pi pi-times"
              className="p-button-text no-print"
              style={{ position: "absolute", top: 8, right: 8 }}
              onClick={() => setShowShippingLabel(false)}
            />
            <ShippingLabel />
            <div className="flex justify-center gap-4 mt-4 no-print">
              <Button
                label="Print Shipping Label"
                icon="pi pi-print"
                className="p-primary-btn"
                onClick={handlePrintShippingLabel}
              />
              <Button
                label="Cancel"
                icon="pi pi-times"
                className="p-primary-btn"
                onClick={() => setShowShippingLabel(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Bulk Shipping Label Modal */}
      {showBulkShippingLabel && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.4)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: 32,
              borderRadius: 8,
              boxShadow: "0 2px 16px rgba(0,0,0,0.2)",
              position: "relative",
              width: "90%",
              height: "90%",
              overflow: "auto",
            }}
            className="bulk-shipping-modal"
          >
            <Button
              icon="pi pi-times"
              className="p-button-text no-print"
              style={{ position: "absolute", top: 8, right: 8 }}
              onClick={() => setShowBulkShippingLabel(false)}
            />
            <div className="text-center mb-4 no-print">
              <p className="text-gray-600"></p>
            </div>
            <div className="bulk-shipping-labels w-full h-full">
              <ShippingLabel
                orders={bulkOrders}
                companyDetails={setupDetails}
              />
            </div>
          </div>
        </div>
      )}

      {/* Print All Orders Modal */}
      {showPrintAll && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.4)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className="print-all-modal"
            style={{
              background: "#fff",
              padding: 32,
              borderRadius: 8,
              boxShadow: "0 2px 16px rgba(0,0,0,0.2)",
              position: "relative",
              width: "90%",
              height: "90%",
              overflow: "auto",
            }}
          >
            <Button
              icon="pi pi-times"
              className="p-button-text no-print"
              style={{ position: "absolute", top: 8, right: 8, zIndex: 1001 }}
              onClick={() => setShowPrintAll(false)}
            />
            <div className="text-center mb-4 no-print">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Print All Orders</h2>
              <p className="text-gray-600">
                {bulkOrders.length} orders ready for printing
              </p>
            </div>
            <div className="print-all-orders">
              {bulkOrders.map((order, index) => (
                <div key={index} className="order-print-page" style={{ 
                  pageBreakAfter: index < bulkOrders.length - 1 ? 'always' : 'auto',
                  marginBottom: '20px',
                  padding: '20px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}>
                  <div className="order-header" style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '20px',
                    paddingBottom: '10px',
                    borderBottom: '2px solid #e5e7eb'
                  }}>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">Order #{order.order_number || order.id}</h3>
                      <p className="text-gray-600">Date: {new Date(order.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="text-right">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                        order.payment_status === 1 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-200 text-gray-700'
                      }`}>
                        {order.payment_status === 1 ? "Paid" : "Not Paid"}
                      </span>
                      <br />
                      <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 mt-1">
                        {order.status?.replace('_', ' ').toUpperCase() || 'PENDING'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="order-details" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">Customer Information</h4>
                      <p><strong>Name:</strong> {order.name}</p>
                      <p><strong>Phone:</strong> {order.phone}</p>
                      <p><strong>Email:</strong> {order.email || 'N/A'}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">Order Details</h4>
                      <p><strong>Payment Method:</strong> {order.payment_method}</p>
                      <p><strong>Total Amount:</strong> {enableCurrency?.symbol || '$'}{order.total_amount}</p>
                      <p><strong>Shipping:</strong> {enableCurrency?.symbol || '$'}{order.total_shipping || 0}</p>
                    </div>
                  </div>
                  
                  <div className="shipping-address" style={{ marginBottom: '20px' }}>
                    <h4 className="font-semibold text-gray-800 mb-2">Shipping Address</h4>
                    <p className="text-gray-700">{order.shipping_address}</p>
                  </div>
                  
                  {order.products && order.products.length > 0 && (
                    <div className="products-section">
                      <h4 className="font-semibold text-gray-800 mb-2">Products</h4>
                      <div className="products-list">
                        {order.products.map((product, productIndex) => (
                          <div key={productIndex} style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            padding: '8px 0',
                            borderBottom: productIndex < order.products.length - 1 ? '1px solid #f3f4f6' : 'none'
                          }}>
                            <div>
                              <p className="font-medium">{product.name}</p>
                              <p className="text-sm text-gray-600">Qty: {product.pivot?.quantity || 1}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-medium">{enableCurrency?.symbol || '$'}{product.pivot?.price || product.price}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-center gap-4 mt-6 no-print">
              <Button
                label="Print All Orders"
                icon="pi pi-print"
                className="p-primary-btn"
                onClick={() => window.print()}
              />
              <Button
                label="Close"
                icon="pi pi-times"
                className="p-button-secondary"
                onClick={() => setShowPrintAll(false)}
              />
            </div>
          </div>
        </div>
      )}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body * { background: #fff !important; }
          
          /* Hide the modal overlay and show only the content */
          .bulk-shipping-modal {
            position: static !important;
            width: 100% !important;
            height: auto !important;
            max-width: none !important;
            max-height: none !important;
            overflow: visible !important;
            background: white !important;
            padding: 0 !important;
            margin: 0 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
          }
          
          /* Print All Orders Modal Styles */
          .print-all-modal {
            position: static !important;
            width: 100% !important;
            height: auto !important;
            max-width: none !important;
            max-height: none !important;
            overflow: visible !important;
            background: white !important;
            padding: 0 !important;
            margin: 0 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
          }
          
          /* Force each order to be on its own page */
          .order-print-page {
            page-break-after: always !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            margin-bottom: 0 !important;
            padding: 20px !important;
            border: 1px solid #000 !important;
            border-radius: 0 !important;
          }
          
          .order-print-page:last-child {
            page-break-after: avoid !important;
          }
          
          /* Ensure proper spacing for print */
          .print-all-orders {
            margin: 0 !important;
            padding: 0 !important;
          }
          
          /* Force each shipping label to be on its own page */
          .shipping-label-page {
            page-break-after: always !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            margin-bottom: 0 !important;
            padding: 0 !important;
          }
          
          .shipping-label-page:last-child {
            page-break-after: avoid !important;
          }
          
          /* Ensure shipping label container fits properly */
          .bulk-shipping-labels .shipping-label-container {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          
          /* Remove any margins/padding that might cause issues */
          .bulk-shipping-labels {
            margin: 0 !important;
            padding: 0 !important;
          }
        }
      `}</style>
    </div>
  );
}
